import type { UserActor } from "../../actor";
import type { Db } from "../../drizzle";
import { z } from "zod/v4";
import { os } from "@orpc/server";
import { and, asc, desc, eq, inArray, isNotNull, isNull, ne, or, sql } from "drizzle-orm";
import { food, foodMeta, foodServing } from "../food/food.sql";
import { Food } from "../food";
import { FoodCategory } from "../food-category";
import { recipeVariation } from "../recipe-variation/recipe-variation.sql";

/**
 * Food search boundary for ranked retrieval, grouping, and serving hydration.
 *
 * Search remains separate from the broader Food domain so callers that only need
 * retrieval logic do not depend on the full food namespace surface.
 */
export namespace FoodSearch {
  export interface SearchParams {
    text: string;
    actor: UserActor;
    foodTypes?: Food.Type[];
    collectionId?: string;
    limit?: number;
    categories?: FoodCategory.Filter;
    useWordSimilarityGate?: boolean;
  }

  export type SearchInfo = Food.Info;
  export type SearchResult = SearchInfo & {
    similarityName: number;
    similarity: number;
    normalizedBaseName: string;
    modifierText: string;
  };

  const DEFAULT_SEARCH_LIMIT = 10;
  const MAX_SEARCH_LIMIT = 50;
  const MAX_SEARCH_TEXT_LENGTH = 500;
  const WORD_SIMILARITY_THRESHOLD = 0.4;

  function normalizeBaseName(name: string) {
    return name.trim().toLowerCase().replace(/\s+/g, " ");
  }

  function compareSearchResults(a: SearchResult, b: SearchResult) {
    if (a.similarityName !== b.similarityName) {
      return b.similarityName - a.similarityName;
    }
    if (a.similarity !== b.similarity) {
      return b.similarity - a.similarity;
    }
    if (a.normalizedBaseName !== b.normalizedBaseName) {
      return a.normalizedBaseName.localeCompare(b.normalizedBaseName);
    }
    if (a.modifierText !== b.modifierText) {
      return a.modifierText.localeCompare(b.modifierText);
    }
    return a.id.localeCompare(b.id);
  }

  type SearchGroupKey = {
    collectionId: string;
    normalizedBaseName: string;
  };

  const foodNameWithModifiers = sql<string>`(${food.name} || COALESCE(', ' || immutable_array_to_string(${food.modifiers}, ', '), ''))`;
  const normalizedBaseNameSql = sql<string>`lower(regexp_replace(trim(${food.name}), '\\s+', ' ', 'g'))`;
  const modifierTextSql = sql<string>`COALESCE(immutable_array_to_string(${food.modifiers}, ', '), '')`;
  const recipeVariationJoinCondition = and(
    eq(recipeVariation.id, food.id),
    eq(recipeVariation.version, food.version),
  );

  function infoSelect() {
    return {
      ...Food.infoSelect(),
      similarityName: sql<number>`0`,
      similarity: sql<number>`0`,
      normalizedBaseName: normalizedBaseNameSql,
      modifierText: modifierTextSql,
    };
  }

  function baseSearchWhereConditions(args: Omit<SearchParams, "text" | "limit">) {
    const effectiveFoodTypes =
      args.foodTypes ?? (["generic", "branded", "recipe", "recipe_variation"] as const);

    const whereConditions = [
      eq(foodMeta.discoverability, "listed"),
      inArray(foodMeta.type, effectiveFoodTypes),
      isNotNull(foodMeta.currentVersion),
      eq(foodMeta.currentVersion, food.version),
      isNull(foodMeta.timeArchived),
      Food.viewableCondition(args.actor),
      ...FoodCategory.filterConditions({
        filter: args.categories,
        foodId: food.id,
        foodVersion: food.version,
      }),
    ];

    if (effectiveFoodTypes.includes("recipe_variation")) {
      const recipeVariationCondition = or(
        ne(foodMeta.type, "recipe_variation"),
        eq(recipeVariation.kind, "custom"),
      );
      if (recipeVariationCondition) {
        whereConditions.push(recipeVariationCondition);
      }
    }

    if (args.collectionId) {
      whereConditions.push(eq(foodMeta.collectionId, args.collectionId));
    }

    return whereConditions;
  }

  export async function search(db: Db, params: SearchParams): Promise<SearchResult[]> {
    const {
      text,
      actor,
      foodTypes,
      collectionId,
      limit,
      categories,
      useWordSimilarityGate = true,
    } = params;

    const trimmedText = text.trim();
    if (!trimmedText) {
      return [];
    }

    const searchText = trimmedText.slice(0, MAX_SEARCH_TEXT_LENGTH);
    const effectiveLimit = Math.min(limit ?? DEFAULT_SEARCH_LIMIT, MAX_SEARCH_LIMIT);
    const effectiveFoodTypes =
      foodTypes ?? (["generic", "branded", "recipe", "recipe_variation"] as const);

    if (effectiveFoodTypes.length === 0) {
      return [];
    }

    const results = await db.transaction(
      async (tx) => {
        await tx.execute(
          sql.raw(`SET LOCAL pg_trgm.word_similarity_threshold = ${WORD_SIMILARITY_THRESHOLD}`),
        );

        const similarityName = sql<number>`similarity(${searchText}, ${food.name})`;
        const similarity = sql<number>`similarity(${searchText}, ${foodNameWithModifiers})`;

        const whereConditions = [
          ...baseSearchWhereConditions({
            actor,
            foodTypes: [...effectiveFoodTypes],
            collectionId,
            categories,
          }),
        ];

        if (useWordSimilarityGate) {
          whereConditions.unshift(sql`${searchText} <% ${foodNameWithModifiers}`);
        }

        return tx
          .select({
            ...infoSelect(),
            similarityName,
            similarity,
            normalizedBaseName: normalizedBaseNameSql,
            modifierText: modifierTextSql,
          })
          .from(foodMeta)
          .innerJoin(food, Food.currentVersionJoinCondition())
          .leftJoin(recipeVariation, recipeVariationJoinCondition)
          .where(and(...whereConditions))
          .orderBy(
            desc(similarityName),
            desc(similarity),
            asc(normalizedBaseNameSql),
            asc(modifierTextSql),
            asc(food.id),
          )
          .limit(effectiveLimit)
          .execute();
      },
      { isolationLevel: "read committed" },
    );

    return results;
  }

  export type SearchItem = Omit<Food.Item, "food"> & {
    food: SearchInfo;
  };

  export type SearchGroupedItem = SearchItem & {
    preparations: SearchItem[];
  };

  export interface SearchResponse {
    generic: SearchGroupedItem[];
    branded: SearchGroupedItem[];
    recipes: SearchGroupedItem[];
  }

  type FoodVersionRef = { id: string; version: number };
  type FoodVersionKey = `${string}:${number}`;
  const foodVersionKey = (ref: FoodVersionRef): FoodVersionKey => `${ref.id}:${ref.version}`;

  export async function getDefaultServingsForFoodVersions(
    db: Db,
    foods: readonly FoodVersionRef[],
  ): Promise<Map<FoodVersionKey, Food.Serving>> {
    if (foods.length === 0) {
      return new Map();
    }

    const uniqueRefs = new Map<string, FoodVersionRef>();
    for (const ref of foods) {
      uniqueRefs.set(foodVersionKey(ref), ref);
    }
    const refs = [...uniqueRefs.values()];

    const whereCondition = or(
      ...refs.map((ref) =>
        and(eq(foodServing.foodId, ref.id), eq(foodServing.foodVersion, ref.version)),
      ),
    );

    const servings = await db
      .selectDistinctOn([foodServing.foodId, foodServing.foodVersion])
      .from(foodServing)
      .where(whereCondition)
      .orderBy(asc(foodServing.foodId), asc(foodServing.foodVersion), asc(foodServing.sortIndex))
      .execute();

    const defaultServingByFoodVersion = new Map<FoodVersionKey, Food.Serving>();
    for (const serving of servings) {
      defaultServingByFoodVersion.set(
        foodVersionKey({ id: serving.foodId, version: serving.foodVersion }),
        serving,
      );
    }

    return defaultServingByFoodVersion;
  }

  export async function getDefaultServingsForFoods(
    db: Db,
    foodIds: string[],
  ): Promise<Map<string, Food.Serving>> {
    if (foodIds.length === 0) {
      return new Map();
    }

    const currentFoodVersions = await db
      .select({ id: foodMeta.id, version: foodMeta.currentVersion })
      .from(foodMeta)
      .where(and(inArray(foodMeta.id, foodIds), isNotNull(foodMeta.currentVersion)))
      .execute();

    const currentVersionRefs = currentFoodVersions.flatMap((ref) =>
      ref.version == null ? [] : [{ id: ref.id, version: ref.version }],
    );

    const defaultServingByFoodVersion = await getDefaultServingsForFoodVersions(
      db,
      currentVersionRefs,
    );

    const defaultServingByFoodId = new Map<string, Food.Serving>();
    for (const ref of currentVersionRefs) {
      const serving = defaultServingByFoodVersion.get(foodVersionKey(ref));
      if (serving) {
        defaultServingByFoodId.set(ref.id, serving);
      }
    }

    return defaultServingByFoodId;
  }

  function searchResultsToItemsWithServings(
    searchResults: SearchResult[],
    defaultServings: Map<FoodVersionKey, Food.Serving>,
  ): SearchItem[] {
    const items: SearchItem[] = [];
    for (const result of searchResults) {
      const serving = defaultServings.get(
        foodVersionKey({ id: result.id, version: result.version }),
      );
      if (serving) {
        items.push({
          amount: serving.defaultAmount,
          food: result,
          serving,
        });
      }
    }
    return items;
  }

  async function searchSiblingPreparationsForGroups(
    db: Db,
    params: SearchParams,
    groupKeys: SearchGroupKey[],
  ): Promise<SearchResult[]> {
    if (groupKeys.length === 0) {
      return [];
    }

    const { actor, foodTypes, collectionId, categories } = params;
    const effectiveFoodTypes =
      foodTypes ?? (["generic", "branded", "recipe", "recipe_variation"] as const);

    const whereConditions = [
      ...baseSearchWhereConditions({
        actor,
        foodTypes: [...effectiveFoodTypes],
        collectionId,
        categories,
      }),
      or(
        ...groupKeys.map((groupKey) =>
          and(
            eq(foodMeta.collectionId, groupKey.collectionId),
            eq(normalizedBaseNameSql, groupKey.normalizedBaseName),
          ),
        ),
      ),
    ];

    return db
      .select(infoSelect())
      .from(foodMeta)
      .innerJoin(food, Food.currentVersionJoinCondition())
      .leftJoin(recipeVariation, recipeVariationJoinCondition)
      .where(and(...whereConditions))
      .execute();
  }

  function buildGroupedSearchItems(
    searchResults: SearchResult[],
    defaultServings: Map<FoodVersionKey, Food.Serving>,
  ): SearchGroupedItem[] {
    if (searchResults.length === 0) {
      return [];
    }

    const items = searchResultsToItemsWithServings(searchResults, defaultServings);
    const itemByVersionKey = new Map<FoodVersionKey, SearchItem>();
    for (const item of items) {
      itemByVersionKey.set(foodVersionKey({ id: item.food.id, version: item.food.version }), item);
    }

    const sortedResults = [...searchResults].sort(compareSearchResults);
    const groups = new Map<string, SearchResult[]>();
    for (const result of sortedResults) {
      const key = `${result.meta.collectionId}:${normalizeBaseName(result.name)}`;
      const existing = groups.get(key);
      if (existing) {
        existing.push(result);
      } else {
        groups.set(key, [result]);
      }
    }

    const groupedItems: SearchGroupedItem[] = [];
    for (const groupResults of groups.values()) {
      const parentResult = groupResults[0];
      if (!parentResult) {
        continue;
      }
      const parentItem = itemByVersionKey.get(
        foodVersionKey({ id: parentResult.id, version: parentResult.version }),
      );
      if (!parentItem) {
        continue;
      }
      const preparations: SearchItem[] = [];
      for (let i = 1; i < groupResults.length; i += 1) {
        const groupResult = groupResults[i];
        if (!groupResult) {
          continue;
        }
        const preparationItem = itemByVersionKey.get(
          foodVersionKey({ id: groupResult.id, version: groupResult.version }),
        );
        if (preparationItem) {
          preparations.push(preparationItem);
        }
      }
      groupedItems.push({
        ...parentItem,
        preparations,
      });
    }

    return groupedItems;
  }

  type RouterContext = {
    db: Db;
    actor: UserActor;
  };
  const base = os.$context<RouterContext>();

  const DEFAULT_PER_TYPE_LIMIT = 10;

  export const router = {
    search: base
      .input(
        z.object({
          text: z.string().min(3),
          collectionId: z.uuid().optional(),
          limit: z.number().min(1).max(MAX_SEARCH_LIMIT).optional(),
          categories: FoodCategory.filterSchema.optional(),
        }),
      )
      .handler(async ({ context: { db, actor }, input }): Promise<SearchResponse> => {
        const perTypeLimit = input.limit ?? DEFAULT_PER_TYPE_LIMIT;

        const [genericResults, brandedResults, recipeResults] = await Promise.all([
          search(db, {
            text: input.text,
            actor,
            foodTypes: ["generic"],
            collectionId: input.collectionId,
            limit: perTypeLimit,
            categories: input.categories,
          }),
          search(db, {
            text: input.text,
            actor,
            foodTypes: ["branded"],
            collectionId: input.collectionId,
            limit: perTypeLimit,
            categories: input.categories,
          }),
          search(db, {
            text: input.text,
            actor,
            foodTypes: ["recipe", "recipe_variation"],
            collectionId: input.collectionId,
            limit: perTypeLimit,
            categories: input.categories,
          }),
        ]);

        const genericGroupKeys = [
          ...new Map(
            genericResults.map((result) => [
              `${result.meta.collectionId}:${result.normalizedBaseName}`,
              {
                collectionId: result.meta.collectionId,
                normalizedBaseName: result.normalizedBaseName,
              },
            ]),
          ).values(),
        ];
        const brandedGroupKeys = [
          ...new Map(
            brandedResults.map((result) => [
              `${result.meta.collectionId}:${result.normalizedBaseName}`,
              {
                collectionId: result.meta.collectionId,
                normalizedBaseName: result.normalizedBaseName,
              },
            ]),
          ).values(),
        ];
        const recipeGroupKeys = [
          ...new Map(
            recipeResults.map((result) => [
              `${result.meta.collectionId}:${result.normalizedBaseName}`,
              {
                collectionId: result.meta.collectionId,
                normalizedBaseName: result.normalizedBaseName,
              },
            ]),
          ).values(),
        ];

        const [genericWithSiblings, brandedWithSiblings, recipesWithSiblings] = await Promise.all([
          searchSiblingPreparationsForGroups(
            db,
            {
              text: input.text,
              actor,
              foodTypes: ["generic"],
              collectionId: input.collectionId,
              limit: perTypeLimit,
              categories: input.categories,
            },
            genericGroupKeys,
          ),
          searchSiblingPreparationsForGroups(
            db,
            {
              text: input.text,
              actor,
              foodTypes: ["branded"],
              collectionId: input.collectionId,
              limit: perTypeLimit,
              categories: input.categories,
            },
            brandedGroupKeys,
          ),
          searchSiblingPreparationsForGroups(
            db,
            {
              text: input.text,
              actor,
              foodTypes: ["recipe", "recipe_variation"],
              collectionId: input.collectionId,
              limit: perTypeLimit,
              categories: input.categories,
            },
            recipeGroupKeys,
          ),
        ]);

        const mergeResultsByVersionKey = (
          rankedResults: SearchResult[],
          siblingResults: SearchResult[],
        ): SearchResult[] => {
          const byVersionKey = new Map<FoodVersionKey, SearchResult>();
          for (const siblingResult of siblingResults) {
            byVersionKey.set(
              foodVersionKey({ id: siblingResult.id, version: siblingResult.version }),
              siblingResult,
            );
          }
          for (const rankedResult of rankedResults) {
            byVersionKey.set(
              foodVersionKey({ id: rankedResult.id, version: rankedResult.version }),
              rankedResult,
            );
          }
          return [...byVersionKey.values()];
        };

        const groupedGenericResults = mergeResultsByVersionKey(genericResults, genericWithSiblings);
        const groupedBrandedResults = mergeResultsByVersionKey(brandedResults, brandedWithSiblings);
        const groupedRecipeResults = mergeResultsByVersionKey(recipeResults, recipesWithSiblings);

        // Version-aware to ensure serving nutrients/units align with the returned food version.
        const defaultServings = await getDefaultServingsForFoodVersions(db, [
          ...groupedGenericResults,
          ...groupedBrandedResults,
          ...groupedRecipeResults,
        ]);

        return {
          generic: buildGroupedSearchItems(groupedGenericResults, defaultServings),
          branded: buildGroupedSearchItems(groupedBrandedResults, defaultServings),
          recipes: buildGroupedSearchItems(groupedRecipeResults, defaultServings),
        };
      }),
  };
}
